/**
 * High-level ride start: wires the workout runner, virtual shifting and action hooks into a session,
 * and registers post-ride processing (FTP proposal, planner status).
 */
import { getSettings } from '@/store/settings';
import type { RideMode } from '@/store/settingsModel';
import type { Course } from '@/routes/course';
import { WorkoutRunner } from '@/workouts/runner';
import type { Workout } from '@/workouts/model';
import { computeFtpProposal } from '@/workouts/ftpTest';
import { applyTestedFtp } from '@/store/ftp';
import { getWorkout } from '@/workouts/repo';
import { localDate, plansForDay, setPlanStatus } from '@/plan/repo';
import { actionHooks } from './actions';
import { getSession, onRideSaved, startRide } from './manager';
import type { ActiveRideSnapshot } from './model';
import type { RideSession, VsHook } from './session';

export interface LaunchOptions {
  course?: Course;
  mode?: RideMode;
  workout?: Workout | null;
  plannedId?: string;
  resume?: ActiveRideSnapshot;
  /** Draft reduction source (group rides). */
  draft?: () => number;
}

/** Factory for the VS controller (registered by the VS module in P5). */
let vsFactory: (() => VsHook) | null = null;
export function setVsFactory(f: () => VsHook): void {
  vsFactory = f;
}

let currentRunner: WorkoutRunner | null = null;
export function getRunner(): WorkoutRunner | null {
  return currentRunner;
}

export function launchRide(opts: LaunchOptions): RideSession {
  const settings = getSettings();
  const runner = opts.workout
    ? new WorkoutRunner(opts.workout, () => getSettings().ftp, opts.resume?.workoutElapsedSec ?? 0)
    : null;
  currentRunner = runner;
  const mode: RideMode = opts.mode ?? (runner ? 'erg' : settings.defaultFreeRideMode);
  const vs = vsFactory ? vsFactory() : null;
  actionHooks.shift = vs
    ? (dir) => {
        (vs as VsHook & { shift?: (d: 1 | -1) => void }).shift?.(dir);
        getSession()?.requestControlUpdate();
      }
    : undefined;
  actionHooks.skipInterval = runner ? () => runner.skip() : undefined;
  actionHooks.ergAdjust = runner
    ? (delta) => {
        runner.adjustWatts(delta);
        return true;
      }
    : undefined;
  const so: Parameters<typeof startRide>[0] = { mode, workout: runner, vs };
  if (opts.course) so.course = opts.course;
  if (opts.plannedId) so.plannedId = opts.plannedId;
  if (opts.resume) so.resume = opts.resume;
  if (opts.draft) so.draft = opts.draft;
  const session = startRide(so);
  session.onFinish(() => {
    if (currentRunner === runner) currentRunner = null;
    actionHooks.skipInterval = undefined;
    actionHooks.ergAdjust = undefined;
    actionHooks.shift = undefined;
  });
  return session;
}

/** Resume a crashed ride (re-creates the workout runner at its saved position). */
export async function resumeRide(snap: ActiveRideSnapshot, course?: Course): Promise<RideSession> {
  const workout = snap.workoutId ? await getWorkout(snap.workoutId) : undefined;
  const o: LaunchOptions = { resume: snap, workout: workout ?? null };
  if (course) o.course = course;
  if (snap.plannedId) o.plannedId = snap.plannedId;
  return launchRide(o);
}

let hooksRegistered = false;
export function registerRideHooks(): void {
  if (hooksRegistered) return;
  hooksRegistered = true;
  // FTP tests: a completed test sets the FTP automatically (the only way it changes after onboarding).
  onRideSaved(async (ride, samples, session) => {
    if (!ride.workoutId) return;
    const w = await getWorkout(ride.workoutId);
    if (!w?.special) return;
    const doneReason = session?.workout?.view?.().doneReason ?? null;
    const p = computeFtpProposal(w, samples, ride.laps, doneReason);
    if (!p) {
      ride.ftpTestIncomplete = true;
      return;
    }
    const previousFtp = await applyTestedFtp(p.proposedFtp);
    ride.ftpProposal = { ...p, previousFtp, accepted: true };
  });
  // Planner: mark the planned ride as done (explicit plan, or the same workout planned today)
  onRideSaved(async (ride) => {
    if (ride.plannedId) {
      await setPlanStatus(ride.plannedId, 'done', ride.id);
      return;
    }
    if (!ride.workoutId) return;
    const today = await plansForDay(localDate(new Date(ride.startedAt)));
    const match = today.find((p) => p.workoutId === ride.workoutId && p.status === 'planned');
    if (match) {
      ride.plannedId = match.id;
      await setPlanStatus(match.id, 'done', ride.id);
    }
  });
}
